import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import theme from "../../data/theme";
import {
  searchPlayers, searchTeams, resolveTeam, teamMatchCandidates, saleFromPlayer, saleWarnings,
} from "../../utils/liveBoard";

/**
 * LiveBoardEntry — one compact strip, three fields, never leaves the keyboard.
 *
 * THE SURFACE LIVES OR DIES HERE. He is typing while an auctioneer talks and
 * eleven people wait on him. The Google Sheet this replaces takes about three
 * seconds a pick; anything slower and he goes back to it. It is a STRIP, not a
 * page — the board below is what people look at.
 *
 *   player (fuzzy, last name resolves most) -> Tab -> price -> Tab -> team
 *   (type-ahead, first letters resolve) -> Enter
 *
 * 🔴 COMMIT IS WHAT REMOVES HIM FROM THE BOARD — not blur, not a debounce, not
 * an effect. Blur-to-commit fires when he tabs away to check something and
 * records a half-typed sale.
 *
 * 🔴 THE TEAM FIELD ECHOES WHO IT RESOLVED TO. The player field autocompletes
 * to a full name, but the team field keeps whatever he typed ("ch"), so the
 * only pre-commit signal used to be the Enter button lighting up — which says
 * it matched SOMETHING, not whom. A wrong team is the silent corruption
 * resolveTeam's -1 exists to prevent, so the resolved name is shown.
 */
const MAX_SUGGESTIONS = 6;
const FREE_TEXT_POSITIONS = ["QB", "RB", "WR", "TE", "DEF", "K"];

export default function LiveBoardEntry({
  board, sold, onCommit, onUndo, onRedo, canUndo = false, canRedo = false,
  onNominate, storageFailed, dropUp = false,
}) {
  const [playerQ, setPlayerQ] = useState("");
  const [picked, setPicked] = useState(null);      // the accepted board player, if any
  const [freePos, setFreePos] = useState(null);    // position chosen for an unmatched name
  const [highlight, setHighlight] = useState(0);
  const [price, setPrice] = useState("");
  const [teamQ, setTeamQ] = useState("");
  const [flash, setFlash] = useState(null);

  const playerRef = useRef(null);
  const priceRef = useRef(null);
  const teamRef = useRef(null);

  const suggestions = useMemo(
    () => (picked ? [] : searchPlayers(playerQ, { limit: MAX_SUGGESTIONS, exclude: sold })),
    [playerQ, picked, sold]
  );

  // An unmatched name is not an error. Over 192 picks a miss is close to
  // certain — a rookie, a name spelled differently, a player off our board —
  // and refusing to record it would stop the whole room. He picks a position
  // and it commits as free text with posRank null; roomTemp already skips
  // unpriced sales, so the tally stays correct without special-casing.
  const isFreeText = !picked && playerQ.trim().length >= 2 && suggestions.length === 0;

  const teamHits = useMemo(() => searchTeams(teamQ, board.teamNames), [teamQ, board.teamNames]);
  const teamIndex = useMemo(
    () => (teamQ.trim() ? resolveTeam(teamQ, board.teamNames) : -1),
    [teamQ, board.teamNames]
  );

  const priceNum = price === "" ? NaN : Number(price);
  const priceOk = Number.isFinite(priceNum) && priceNum >= 0;
  const playerOk = !!board.nomination || !!picked || (isFreeText && !!freePos);
  const ready = playerOk && priceOk && teamIndex >= 0;

  // STAGE 2. The player on the block IS the sale — the field can be empty, or
  // mid-edit for the next nomination, and the price and winner still belong to
  // him. Falling back to the field keeps the one-stage path working for anyone
  // who never nominates, which is a real way to use this at speed.
  const effectivePlayer = useMemo(
    () => board.nomination
      || picked
      || (isFreeText && freePos ? { name: playerQ.trim(), pos: freePos, posRank: null } : null),
    [board.nomination, picked, isFreeText, freePos, playerQ]
  );

  const pending = useMemo(
    () => (ready ? saleFromPlayer(effectivePlayer, priceNum, teamIndex, 0) : null),
    [ready, effectivePlayer, priceNum, teamIndex]
  );
  const warnings = useMemo(() => saleWarnings(board, pending), [board, pending]);

  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 WHY THE ROW WILL NOT COMMIT, IN WORDS. Ben's rehearsal, on a board with
   * three teams whose names start with B: he typed a team the board did not
   * have and the row silently refused. His words were "it is stuck", which is
   * the correct reading of what the UI showed him — `ready === false` greys the
   * Enter button and that was the entire feedback. `saleWarnings` structurally
   * cannot help: it takes a `pending` sale, which does not exist until the row
   * already resolves, so the one state that most needs an explanation is the
   * one guaranteed to produce no message.
   *
   * At 9pm with eleven people waiting, a row that refuses without saying why is
   * the worst failure this surface has — worse than a wrong number, because
   * there is no next action to take.
   *
   * 🔴 THIS EXPLAINS THE GATE, IT DOES NOT WIDEN IT. `ready` is untouched and
   * `resolveTeam`'s -1 on ambiguity is load-bearing: winning a player for the
   * wrong team is silent and corrupts every derived number. Nothing here can
   * make a row committable that was not committable before.
   *
   * ⚠️ ONLY A FIELD WITH TEXT IN IT IS EVER SCOLDED, and only when there is no
   * obvious next action already on screen. An empty row says nothing; a row
   * with the suggestion menu open says nothing either, because the menu IS the
   * instruction. Get this wrong and the bar nags through all 168 picks, which
   * would be its own defect.
   *
   * ORDER IS FIELD ORDER — player, then price, then team. One reason at a time:
   * three simultaneous scoldings is noise, and the first thing to fix is the
   * thing to say.
   * ══════════════════════════════════════════════════════════════════════
   */
  const reason = useMemo(() => {
    const q = playerQ.trim();
    // PLAYER. Suppressed while suggestions are up — he is choosing from a list
    // that is already telling him what to do. What is left is the dead end:
    // a name our board does not have.
    if (q && !playerOk && suggestions.length === 0) {
      return isFreeText
        ? `no player called "${q}". Pick a position below to enter him as typed`
        : `no player matches "${q}"`;
    }
    // PRICE. An EMPTY price is the failure here, so unlike the other two this
    // cannot key on "has text" — it is gated on the other two fields being
    // settled instead, which is what keeps it off an empty row.
    if (playerOk && teamIndex >= 0 && !priceOk) return "enter a price";
    // TEAM. The reported case.
    if (teamQ.trim() && teamIndex < 0) {
      // 🔴 THE CANDIDATES COME FROM THE MATCHER, NOT FROM A SECOND SEARCH.
      // These are the rows that DECIDED the refusal — see teamMatchCandidates.
      const hits = teamMatchCandidates(teamQ, board.teamNames).map(t => t.name);
      if (hits.length > 1) {
        const list = hits.length === 2
          ? hits.join(" and ")
          : `${hits.slice(0, -1).join(", ")} and ${hits[hits.length - 1]}`;
        return `"${teamQ.trim()}" matches ${list}. Type another letter`;
      }
      // No match at all. Naming the teams turns a dead end into a keystroke;
      // a dozen names would not fit the strip, so it is a sample.
      const all = board.teamNames;
      const sample = all.slice(0, 4).join(", ") + (all.length > 4 ? "…" : "");
      return `no team called "${teamQ.trim()}". The teams are ${sample}`;
    }
    return null;
  }, [playerQ, playerOk, suggestions.length, isFreeText, priceOk, teamQ, teamIndex, board.teamNames]);

  const clearRow = useCallback(() => {
    setPlayerQ(""); setPicked(null); setFreePos(null); setHighlight(0); setPrice(""); setTeamQ("");
  }, []);

  /**
   * 🔴 ACCEPTING A PLAYER IS NOMINATING HIM. The nominate call lives HERE, on
   * the event, and not on the callers — that is the whole fix for the Aug 11
   * P0, where the mouse path accepted a player and never put him up. Ben ran
   * the board for real and the block sat on "BEN'S NOMINATION" all night while
   * the sales committed fine underneath it.
   *
   * It was intermittent BY INPUT METHOD, which is the worst way for this to
   * fail: keyboard-only worked, so it read as a fluke rather than as a path.
   * Bolting a second call onto the mouse handler would have restored exactly
   * that shape — the next accept path added (a paste, a barcode, a tap) would
   * silently not nominate either. Every caller now nominates by construction.
   */
  const acceptPlayer = useCallback((p) => {
    if (!p) return false;
    setPicked(p); setPlayerQ(p.name); setFreePos(null); setHighlight(0);
    if (onNominate) onNominate(p);
    return true;
  }, [onNominate]);

  /**
   * The free-text twin. An unmatched name is not a player until a position is
   * chosen, so THAT is the moment he becomes accepted — and therefore the
   * moment he goes on the block. Same rule, same single site: the position
   * button and the Enter path both come through here.
   */
  const acceptFreeText = useCallback((pos) => {
    const name = playerQ.trim();
    if (!name || !pos) return false;
    setFreePos(pos);
    if (onNominate) onNominate({ name, pos, posRank: null });
    return true;
  }, [playerQ, onNominate]);

  const commit = useCallback(() => {
    if (!ready) return;
    if (onCommit(effectivePlayer, priceNum, teamIndex)) {
      setFlash({ name: effectivePlayer.name, price: priceNum, team: board.teamNames[teamIndex] });
      clearRow();
      playerRef.current?.focus();
    }
  }, [ready, onCommit, effectivePlayer, priceNum, teamIndex, board.teamNames, clearRow]);

  // Enter from any field: commit when complete, otherwise advance to the first
  // thing still missing. Tab and Enter behave identically on the player field.
  const advanceOrCommit = useCallback(() => {
    if (ready) { commit(); return; }
    if (!playerOk) { playerRef.current?.focus(); return; }
    if (!priceOk) { priceRef.current?.focus(); return; }
    teamRef.current?.focus();
  }, [ready, commit, playerOk, priceOk]);

  // STAGE 1. Accepting a match also puts him ON THE BLOCK, which is why the
  // two-stage flow costs no extra keystrokes: the player field already needed
  // one Enter to accept a match, and that same press now also nominates.
  //
  // Nothing here re-checks `picked`: `suggestions` is already empty whenever a
  // player is accepted, so there is nothing to accept twice. The old `!picked`
  // guard was that same condition spelled a second time, and reading as though
  // it were the thing preventing a double nomination is what made the missing
  // nominate call on the mouse path look survivable.
  const acceptAndAdvance = useCallback(() => {
    const match = suggestions[highlight];
    if (match) {
      acceptPlayer(match);
      priceRef.current?.focus();
      return true;
    }
    if (isFreeText && freePos) {
      acceptFreeText(freePos);
      priceRef.current?.focus();
      return true;
    }
    return false;
  }, [suggestions, highlight, acceptPlayer, acceptFreeText, isFreeText, freePos]);

  // Once he is on the block the player field has done its job. Clearing it
  // means the next nomination can be typed while the current one is still up.
  useEffect(() => { if (board.nomination) { setPlayerQ(""); setPicked(null); setFreePos(null); } }, [board.nomination]);

  const onPlayerKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
    if (e.key === "Tab" && !e.shiftKey) {
      if (acceptAndAdvance()) e.preventDefault();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (acceptAndAdvance()) return;
      advanceOrCommit();
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); clearRow(); }
  };

  const onPriceKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); if (ready) commit(); else teamRef.current?.focus(); return; }
    if (e.key === "Escape") { e.preventDefault(); clearRow(); playerRef.current?.focus(); }
  };

  const onTeamKey = (e) => {
    if (e.key === "Tab" && !e.shiftKey && teamHits.length && teamIndex < 0) {
      e.preventDefault();
      setTeamQ(board.teamNames[teamHits[0].index]);
      return;
    }
    if (e.key === "Enter") { e.preventDefault(); advanceOrCommit(); return; }
    if (e.key === "Escape") { e.preventDefault(); clearRow(); playerRef.current?.focus(); }
  };

  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 ONE HANDLER FOR BOTH CHORDS. This used to be a Ctrl/Cmd+Z-only listener
   * for the depth-one undo; it was REPLACED rather than joined by a second,
   * because two window listeners on the same chord would undo twice per press
   * and the second undo is invisible — the board just loses an extra pick.
   *
   * Ctrl/Cmd+Z undoes, Ctrl/Cmd+Shift+Z and Ctrl+Y redo. Ctrl+Y is here because
   * it is what Windows hands people, and this is a Windows laptop on draft
   * night.
   *
   * 🔴 TWO GUARDS, AND BOTH ARE ABOUT NOT STEALING A KEYSTROKE MID-TYPE:
   *   1. A field with text in it keeps its own undo — a text cursor implies
   *      the browser's undo, and taking that away mid-name is worse than
   *      having no chord at all.
   *   2. A SUGGESTION MENU OPEN means he is in the middle of choosing a player.
   *      The menu is driven by what is in the field, so guard 1 covers this in
   *      practice today — it is written out anyway because the two conditions
   *      are different claims, and a future field that opens a menu while empty
   *      would silently lose this protection.
   * ══════════════════════════════════════════════════════════════════════
   */
  const menuOpen = suggestions.length > 0 || isFreeText;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = (e.key || "").toLowerCase();
      const isUndo = k === "z" && !e.shiftKey;
      const isRedo = (k === "z" && e.shiftKey) || k === "y";
      if (!isUndo && !isRedo) return;
      if (menuOpen) return;
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && el.value) return;
      e.preventDefault();
      const res = isUndo ? onUndo() : onRedo && onRedo();
      if (res && res.ok) {
        const sale = res.sale;
        setFlash(sale
          ? { undone: isUndo, name: sale.name, price: sale.price, team: board.teamNames[sale.team] }
          : { bare: true, undone: isUndo });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onUndo, onRedo, menuOpen, board.teamNames]);

  useEffect(() => { playerRef.current?.focus(); }, []);

  const field = {
    background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
    borderRadius: theme.radius.md, color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.sub, padding: "8px 11px", outline: "none", width: "100%",
  };
  const label = {
    fontSize: theme.fontSize.nano, textTransform: "uppercase", letterSpacing: "0.08em",
    color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold, marginBottom: 2, display: "block",
  };
  // 🔴 DIRECTION IS LOAD-BEARING WHEN THE ROW IS PINNED TO THE BOTTOM EDGE.
  // A menu anchored `top: 100%` opens BELOW the field — off the bottom of the
  // viewport, with nothing to scroll to, so the suggestions are simply
  // invisible and the only way to pick a player is to type his full name. It
  // fails silently, which is the worst way for this particular row to fail.
  // One path for the button and the chord, so the two can never drift on what
  // a successful undo shows.
  const runHistory = (fn, isUndo) => {
    const res = fn && fn();
    if (res && res.ok) {
      const sale = res.sale;
      setFlash(sale
        ? { undone: isUndo, name: sale.name, price: sale.price, team: board.teamNames[sale.team] }
        : { bare: true, undone: isUndo });
    }
    playerRef.current?.focus();
  };
  const histBtn = (enabled) => ({
    background: "transparent", border: `1px solid ${enabled ? theme.colors.border.default : theme.colors.border.subtle}`,
    borderRadius: theme.radius.md, color: enabled ? theme.colors.text.muted : theme.colors.border.default,
    fontFamily: "inherit", fontSize: theme.fontSize.caption, padding: "9px 12px",
    cursor: enabled ? "pointer" : "default", whiteSpace: "nowrap", height: 36,
  });
  const menu = {
    position: "absolute", left: 0, right: 0, zIndex: 60,
    ...(dropUp ? { bottom: "100%", marginBottom: 6 } : { top: "100%", marginTop: 4 }),
    background: theme.colors.bg.elevated, border: `1px solid ${theme.colors.border.default}`,
    borderRadius: theme.radius.md, overflow: "hidden",
    boxShadow: dropUp ? "0 -12px 32px rgba(0,0,0,0.55)" : "0 12px 32px rgba(0,0,0,0.5)",
  };

  return (
    <div style={dropUp ? { display: "flex", flexDirection: "column" } : undefined}>
      {storageFailed && (
        <div style={{
          ...(dropUp ? { order: -2 } : null),
          background: "rgba(239,68,68,0.12)", border: `1px solid ${theme.colors.status.danger}`,
          borderRadius: theme.radius.md, padding: "8px 12px", marginBottom: 8,
          color: "#fecaca", fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
        }}>
          ⚠️ This board is NOT saving to this device. Keep the tab open. A refresh will lose it.
        </div>
      )}

      <div style={{
        display: "grid", gridTemplateColumns: "minmax(0,3fr) 92px minmax(0,2fr) auto",
        gap: 8, alignItems: "end",
      }}>
        {/* ---- PLAYER ---- */}
        <div style={{ position: "relative", minWidth: 0 }}>
          <label style={label} htmlFor="lb-player">Player</label>
          <input
            id="lb-player" ref={playerRef} value={playerQ} onKeyDown={onPlayerKey}
            onChange={e => { setPlayerQ(e.target.value); setPicked(null); setFreePos(null); setHighlight(0); }}
            placeholder="last name…" autoComplete="off" spellCheck={false}
            style={{ ...field, borderColor: playerOk ? theme.colors.status.success : theme.colors.border.default }}
          />
          {suggestions.length > 0 && (
            <div data-testid="player-menu" style={menu}>
              {suggestions.map((p, i) => (
                <div
                  key={`${p.name}-${p.pos}`}
                  onMouseDown={(e) => { e.preventDefault(); acceptPlayer(p); priceRef.current?.focus(); }}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    padding: "7px 11px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                    background: i === highlight ? theme.helpers.brandBg(0.16) : "transparent",
                  }}
                >
                  <span style={{
                    ...theme.helpers.posStyle(p.pos), fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                    padding: "2px 6px", borderRadius: 4, minWidth: 28, textAlign: "center",
                  }}>{p.pos}</span>
                  <span style={{ color: theme.colors.text.primary, fontSize: theme.fontSize.body }}>{p.name}</span>
                  <span style={{ color: theme.colors.text.faint, fontSize: theme.fontSize.micro, marginLeft: "auto" }}>
                    {p.team}{p.posRank ? ` · ${p.posRank}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
          {isFreeText && (
            <div style={menu}>
              <div style={{ padding: "6px 11px", fontSize: theme.fontSize.micro, color: theme.colors.text.faint }}>
                Not on our board. Pick a position to record him anyway
              </div>
              <div style={{ display: "flex", gap: 4, padding: "0 11px 8px", flexWrap: "wrap" }}>
                {FREE_TEXT_POSITIONS.map(pos => (
                  <button
                    key={pos} type="button"
                    onMouseDown={(e) => { e.preventDefault(); acceptFreeText(pos); priceRef.current?.focus(); }}
                    aria-label={`Record as ${pos}`}
                    style={{
                      ...theme.helpers.posStyle(pos),
                      opacity: freePos && freePos !== pos ? 0.4 : 1,
                      fontSize: theme.fontSize.micro, fontWeight: theme.fontWeight.heavy,
                      padding: "3px 9px", borderRadius: 5, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >{pos}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- PRICE ---- */}
        <div>
          <label style={label} htmlFor="lb-price">Price</label>
          <input
            id="lb-price" ref={priceRef} value={price} onKeyDown={onPriceKey}
            onChange={e => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="$" inputMode="numeric" autoComplete="off"
            style={{ ...field, textAlign: "center", fontWeight: theme.fontWeight.bold }}
          />
        </div>

        {/* ---- TEAM ---- */}
        <div style={{ position: "relative", minWidth: 0 }}>
          {/* The echo sits BESIDE the label, not inside it: nested in the
              label it became part of the field's accessible name, which read
              "Team→ Chris" to a screen reader and to anything querying by
              label. */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <label style={label} htmlFor="lb-team">Team</label>
            {teamIndex >= 0 && (
              <span style={{
                color: theme.colors.status.success, fontSize: theme.fontSize.nano,
                fontWeight: theme.fontWeight.heavy, marginBottom: 2,
              }}>→ {board.teamNames[teamIndex]}</span>
            )}
          </div>
          <input
            id="lb-team" ref={teamRef} value={teamQ} onKeyDown={onTeamKey}
            onChange={e => setTeamQ(e.target.value)}
            placeholder="first letters…" autoComplete="off" spellCheck={false}
            style={{ ...field, borderColor: teamIndex >= 0 ? theme.colors.status.success : theme.colors.border.default }}
          />
          {teamQ && teamIndex < 0 && teamHits.length > 0 && (
            <div style={menu}>
              {teamHits.slice(0, 6).map(t => (
                <div key={t.index}
                  onMouseDown={(e) => { e.preventDefault(); setTeamQ(t.name); }}
                  style={{ padding: "7px 11px", cursor: "pointer", color: theme.colors.text.primary, fontSize: theme.fontSize.body }}
                >{t.name}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={commit} disabled={!ready}
            style={{
              background: ready ? theme.colors.brand.gradientBtn : theme.colors.bg.raised,
              color: ready ? "#fff" : theme.colors.text.faint,
              border: "none", borderRadius: theme.radius.md, padding: "9px 16px",
              fontFamily: "inherit", fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.heavy,
              cursor: ready ? "pointer" : "default", whiteSpace: "nowrap", height: 36,
            }}
          >Enter ↵</button>
          {/* The visible half of undo/redo. The chords are the fast path, but a
              control you can see is what makes them discoverable at 8pm with
              eleven people waiting.

              🔴 DISABLED FROM THE STACK, NOT FROM `board.sales.length`. Undo now
              reaches every verb — a nomination, a rename, a clock change — so a
              button gated on "are there sales" would be dead on a board where
              there is plainly something to undo. A dead button is honest; a
              live button that does nothing is not. */}
          <button
            onClick={() => { runHistory(onUndo, true); }}
            disabled={!canUndo}
            aria-label="Undo"
            style={histBtn(canUndo)}
          >Undo</button>
          <button
            onClick={() => { runHistory(onRedo, false); }}
            disabled={!canRedo}
            aria-label="Redo"
            style={histBtn(canRedo)}
          >Redo</button>
        </div>
      </div>

      {/* Warnings DESCRIBE and never block — the auctioneer is the source of
          truth, and a board that refuses a real sale is useless at the one
          moment it matters. */}
      {/* 🔴 A REASON AND A WARNING CANNOT COLLIDE — they are mutually exclusive
          BY CONSTRUCTION, not by precedence. `pending` is null unless `ready`,
          and `saleWarnings` returns [] for a null sale, so warnings exist only
          when the row WOULD commit; a reason exists only when it would not.
          They are rendered at the same level and both outrank the undo flash,
          which is the one thing that genuinely can co-occur with either. A test
          pins the exclusivity, because "they never both appear" is the kind of
          claim that is true until somebody makes warnings work on a partial
          row. */}
      {(warnings.length > 0 || reason || flash) && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", minHeight: 18,
          ...(dropUp ? { marginBottom: 4, order: -1 } : { marginTop: 6 }),
        }}>
          {warnings.map(w => (
            <span key={w} style={{
              fontSize: theme.fontSize.micro, color: "#fde68a",
              background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.35)",
              borderRadius: 999, padding: "2px 9px",
            }}>{w}</span>
          ))}
          {reason && (
            <span data-testid="entry-reason" style={{
              fontSize: theme.fontSize.micro, color: "#fecaca",
              background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)",
              borderRadius: 999, padding: "2px 9px",
            }}>{reason}</span>
          )}
          {flash && !warnings.length && !reason && (
            <span style={{ fontSize: theme.fontSize.micro, color: theme.colors.text.faint }}>
              {/* 🔴 A NAMELESS UNDO SAYS SO RATHER THAN INVENTING A NAME. Undo
                  covers every verb now, and most of them do not remove a sale —
                  `res.sale` is only set when the two board states differ by
                  exactly one trailing record. */}
              {flash.bare
                ? (flash.undone ? "undid the last change" : "redid the last change")
                : <>
                  {flash.undone ? "undid" : "added"}{" "}
                  <strong style={{ color: theme.colors.text.secondary }}>{flash.name}</strong>, ${flash.price} to {flash.team}
                </>}
              <span style={{ marginLeft: 8, opacity: 0.7 }}>⌘/Ctrl+Z undo · ⇧⌘/Ctrl+Shift+Z redo</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
